import { useState } from "react";
import { useSnapshot } from "valtio";
import {
  Sun,
  Moon,
  Monitor,
  User,
  Shield,
  Code,
  Lock,
  Unlock,
  RefreshCw,
  LogOut,
  Cloud,
  CloudOff,
  Palette,
  Globe,
} from "lucide-react";
import { useLocale, useTranslation } from "@vizi/localization/react";
import { db } from "@/lib/instantdb";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useSettings, type ThemeOption } from "@/lib/stores/settings";
import {
  developerStore,
  unlockDeveloperMode,
  lockDeveloperMode,
  setInstantDbDevtools,
  setUseMockBackend,
} from "@/lib/stores/developer";
import { openThemeEditor } from "@/lib/stores/theme-editor";

const themeOptions: { value: ThemeOption; label: string; icon: typeof Sun }[] =
  [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ];

// Track the initial devtools value at page load for refresh notification
const initialDevtoolsValue =
  typeof window !== "undefined"
    ? (localStorage
        .getItem("vizi-developer-settings")
        ?.includes('"instantDbDevtools":true') ?? false)
    : false;

export default function Settings() {
  const { user } = db.useAuth();
  const {
    theme,
    syncedFromCloud,
    syncing,
    setTheme,
    analyticsEnabled,
    setAnalyticsEnabled,
  } = useSettings();
  const { developerUnlocked, instantDbDevtools, useMockBackend } =
    useSnapshot(developerStore);
  const { currentLanguage, changeLanguage, languages } = useLocale();
  const { t } = useTranslation("common");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const handleUnlock = () => {
    if (unlockDeveloperMode(password)) {
      setPassword("");
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleUnlock();
    }
  };

  // Show refresh notice if devtools setting differs from what was loaded at init
  const needsRefresh = instantDbDevtools !== initialDevtoolsValue;

  return (
    <div className="flex h-full flex-col">
      {/* Fixed header */}
      <div className="flex-shrink-0 border-b border-border bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <h1 className="text-2xl font-bold text-foreground">
            {t("settings")}
          </h1>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          {/* Account section */}
          <section className="mb-8">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
              <User className="h-5 w-5" />
              {t("account")}
            </h2>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-lg font-medium text-primary">
                    {user?.email?.[0].toUpperCase() ?? "?"}
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{user?.email}</p>
                    <p className="text-sm text-muted-foreground">
                      {t("signedInWithMagicLink")}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => db.auth.signOut()}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("signOut")}
                </Button>
              </div>
            </Card>
          </section>

          {/* Appearance section */}
          <section className="mb-8">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
              <Sun className="h-5 w-5" />
              {t("appearance")}
            </h2>
            <Card className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {t("chooseTheme")}
                </p>
                {user && (
                  <span
                    className={cn(
                      "flex items-center gap-1 text-xs",
                      syncing
                        ? "text-amber-600 dark:text-amber-400"
                        : syncedFromCloud
                          ? "text-green-600 dark:text-green-400"
                          : "text-muted-foreground",
                    )}
                  >
                    {syncing ? (
                      <>
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Syncing...
                      </>
                    ) : syncedFromCloud ? (
                      <>
                        <Cloud className="h-3 w-3" />
                        Synced
                      </>
                    ) : (
                      <>
                        <CloudOff className="h-3 w-3" />
                        Local only
                      </>
                    )}
                  </span>
                )}
              </div>
              <ToggleGroup
                type="single"
                value={theme}
                onValueChange={(value) =>
                  value && setTheme(value as ThemeOption)
                }
                className="flex gap-2"
              >
                {themeOptions.map((option) => (
                  <ToggleGroupItem
                    key={option.value}
                    value={option.value}
                    variant="outline"
                    className="flex-1 gap-2 px-4 py-3"
                  >
                    <option.icon className="h-4 w-4" />
                    {option.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>

              {/* Language Picker */}
              <div className="mt-4 border-t border-border pt-4">
                <div className="mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {t("language")}
                  </p>
                </div>
                <ToggleGroup
                  type="single"
                  value={currentLanguage}
                  onValueChange={(value) =>
                    value && changeLanguage(value as "en" | "es")
                  }
                  className="flex gap-2"
                >
                  {languages.map((lang) => (
                    <ToggleGroupItem
                      key={lang.code}
                      value={lang.code}
                      variant="outline"
                      className="flex-1 gap-2 px-4 py-3"
                    >
                      {lang.name}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
            </Card>
          </section>

          {/* Privacy section */}
          <section className="mb-8">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
              <Shield className="h-5 w-5" />
              {t("privacy")}
            </h2>
            <Card className="space-y-4 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-foreground">
                    {t("usageAnalytics")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("usageAnalyticsDesc")}
                  </p>
                </div>
                <Switch
                  checked={analyticsEnabled}
                  onCheckedChange={setAnalyticsEnabled}
                />
              </div>
            </Card>
          </section>

          {/* Developer section */}
          <section>
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground">
              <Code className="h-5 w-5" />
              {t("developer")}
            </h2>
            <Card className="p-4">
              {!developerUnlocked ? (
                <div>
                  <p className="mb-3 text-sm text-muted-foreground">
                    Enter developer password to access advanced settings
                  </p>
                  <form
                    data-1p-ignore
                    data-lpignore="true"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUnlock();
                    }}
                    className="flex gap-2"
                  >
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError(false);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Password"
                      autoComplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                      className={cn(
                        "flex-1 rounded-lg border bg-background px-3 py-2 text-sm",
                        passwordError ? "border-destructive" : "border-input",
                      )}
                    />
                    <Button type="submit" variant="secondary">
                      <Lock className="mr-1 h-4 w-4" />
                      Unlock
                    </Button>
                  </form>
                  {passwordError && (
                    <p className="mt-2 text-sm text-destructive">
                      Incorrect password
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">
                        InstantDB Devtools
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Show debug overlay in corner of screen
                      </p>
                    </div>
                    <Switch
                      checked={instantDbDevtools}
                      onCheckedChange={setInstantDbDevtools}
                    />
                  </div>

                  {needsRefresh && (
                    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                      <RefreshCw className="h-4 w-4 flex-shrink-0" />
                      Refresh the page to apply devtools changes
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">
                        Use Mock Backend
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Simulate processing without real workers
                      </p>
                    </div>
                    <Switch
                      checked={useMockBackend}
                      onCheckedChange={setUseMockBackend}
                    />
                  </div>

                  {!useMockBackend && (
                    <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                      <Cloud className="h-4 w-4 flex-shrink-0" />
                      Real backend mode: Ensure workers are running locally
                    </div>
                  )}

                  {/* Theme Editor */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">
                        Theme Editor
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Customize shadcn colors in real-time
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openThemeEditor}
                    >
                      <Palette className="mr-1 h-4 w-4" />
                      Open Editor
                    </Button>
                  </div>

                  <div className="border-t border-border pt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={lockDeveloperMode}
                      className="text-muted-foreground"
                    >
                      <Unlock className="mr-1 h-4 w-4" />
                      Lock Developer Settings
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </section>

          {/* About section */}
          <section className="mt-8 border-t border-border pt-8">
            <h2 className="mb-4 text-lg font-semibold text-foreground">
              {t("about")}
            </h2>
            <Card className="p-4">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  <span className="font-medium text-foreground">
                    Vizi Admin
                  </span>{" "}
                  - Cloud administration dashboard
                </p>
                <p>Build: {new Date(__BUILD_DATE__).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Version 0.1.0</p>
              </div>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
}
